import { useEffect, useState } from 'react';

import { DeleteOutlined as DeleteOutlinedIcon, Edit as EditIcon } from '@mui/icons-material';
import { HttpStatusCode } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { ConfirmationDialog, FormDialog, ResourceNotFoundFallback } from '~components';
import { useSnackbar } from '~hooks';
import { DetailsGridLayout, usePageLayoutProperties } from '~layouts';

import AccessRuleGeneralInfoCard from '../components/cards/ruleGeneralInfoCard';
import SkcAccessRuleForm from '../forms/skcRuleForm/skcAccessRuleForm';
import { AccessRuleNew } from '../interfaces/accessRule';
import SkcAccessRulesService from '../services/skcAccessRulesService';

const service = new SkcAccessRulesService();

interface UserAction {
	type: 'delete' | 'edit';
	label?: string;
	id: string;
}

interface AccessRuleDetailsContainerProps {
	id: string;
}

const AccessRuleDetailsContainer = ({ id }: AccessRuleDetailsContainerProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();
	const { setPageTitle, setPageHeaderButtons: setPageButtons } = usePageLayoutProperties();

	const [userAction, setUserAction] = useState<UserAction | null>(null);

	const { data, error } = useSWR([service.basePath, id], ([_, args]) =>
		service.getAccessRuleById(args)
	);

	const { trigger: triggerEdit, isMutating: isEditMutating } = useSWRMutation(
		[service.basePath, id],
		([_, id], { arg }: { arg: AccessRuleNew }) => service.updateAccessRule(id, arg),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.success.message.updated')} ${t('rule').toLowerCase()}`);
				setUserAction(null);
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: triggerDelete, isMutating: isDeleteMutating } = useSWRMutation(
		[service.basePath, id],
		([_, args]) => service.deleteAccessRule(args),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.successfullyDeleted')} ${t('rule').toLowerCase()}`);
				navigate('..', { relative: 'path' });
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	useEffect(() => {
		if (data) {
			setPageTitle(data.label);

			setPageButtons([
				{
					children: t('ui.edit'),
					startIcon: <EditIcon />,
					onClick: () => setUserAction({ type: 'edit', id: id }),
				},
				{
					children: t('ui.delete'),
					color: 'error',
					startIcon: <DeleteOutlinedIcon />,
					onClick: () => setUserAction({ type: 'delete', id: id }),
				},
			]);
		}
	}, [data, id, setPageButtons, setPageTitle, t]);

	if (error?.status === HttpStatusCode.NotFound) {
		return <ResourceNotFoundFallback label={id} type={t('rule')} />;
	}

	return (
		<>
			<DetailsGridLayout>
				<AccessRuleGeneralInfoCard id={id} />
			</DetailsGridLayout>
			<FormDialog
				open={userAction?.type === 'edit'}
				loading={isEditMutating}
				maxWidth='md'
				fullWidth
				onClose={() => setUserAction(null)}
			>
				<SkcAccessRuleForm id={userAction?.id} onSubmit={async (data) => await triggerEdit(data)} />
			</FormDialog>
			<ConfirmationDialog
				open={userAction?.type === 'delete'}
				loading={isDeleteMutating}
				subTitle={t('thisWillDeleteResourceWithName', {
					resource: t('rule'),
					name: userAction?.label ?? userAction?.id,
				})}
				onConfirm={async () => await triggerDelete()}
				onClose={() => setUserAction(null)}
			/>
		</>
	);
};

export default AccessRuleDetailsContainer;
